import {
  HTTP_AP_ListCases_ForApprovalRequiredTable_Request_Filter,
  HTTP_AP_ListCases_ForApprovalRequiredTable_Request_Sort,
  HTTP_AP_ListCases_ForClientDetail_Request_Filter,
  HTTP_AP_ListCases_ForClientDetail_Request_Sort,
  HTTP_AP_ListCases_ForClosedTable_Request_Filter,
  HTTP_AP_ListCases_ForClosedTable_Request_Sort,
  HTTP_AP_ListCases_ForLeadsTable_Request_Filter,
  HTTP_AP_ListCases_ForLeadsTable_Request_Sort,
  HTTP_AP_ListCases_ForOpportunitiesTable_Request_Filter,
  HTTP_AP_ListCases_ForOpportunitiesTable_Request_Sort,
  HTTP_AP_ListCases_ForPipelineTable_Request_Filter,
  HTTP_AP_ListCases_ForPipelineTable_Request_Sort,
} from '@api-new/caseservice';
import {
  HTTP_AP_ListAffiliates_ForTable_Request_Filter,
  HTTP_AP_ListCompanies_ForTable_Request_Filter,
  HTTP_AP_ListCompanies_ForTable_Request_Sort,
  HTTP_AP_ListNetworks_ForTable_Response_Network,
} from '@api-new/companyservice';
import { HTTP_AP_ListMortgageOfferParserBatches_Request_Filter } from '@api-new/documentservice';
import { HTTP_AP_ListImports_Request_Filter, HTTP_AP_ListImports_Request_Sort } from '@api-new/importservice';
import {
  HTTP_AP_LenderGroup,
  HTTP_AP_ListLenderGroups_ForTable_Request_Filter,
  HTTP_AP_ListLenderGroups_ForTable_Request_Sort,
  HTTP_AP_ListLenders_ForTable_Request_Filter,
  HTTP_AP_ListLenders_ForTable_Request_Sort,
  HTTP_AP_ListMortgageProducts_ForTable_Request_Filter,
  HTTP_AP_ListMortgageProducts_ForTable_Request_Sort,
  HTTP_AP_ListMortgageProducts_ForTable_Response_MortgageProduct,
  HTTP_AP_ListMortgages_ForTable_Request_Sort,
  HTTP_AP_ListPreCompletions_ForTable_Request_Filter,
  HTTP_AP_ListPreCompletions_ForTable_Request_Sort,
} from '@api-new/mortgageservice';
import {
  HTTP_AP_HPIOutcodeToRegionEntry,
  HTTP_AP_ListHPIOutcodeToRegionEntries_Request_Filter,
  HTTP_AP_ListHPIOutcodeToRegionEntries_Request_Sort,
  HTTP_AP_ListProperties_ForTable_Request_Filter,
  HTTP_AP_ListProperties_ForTable_Request_Sort,
  HTTP_AP_ListProperties_ForTable_Response_Property,
} from '@api-new/propertyservice';
import { HTTP_AP_ListActivityLogs_Request_Filter, HTTP_AP_ListActivityLogs_Request_Sort } from '@api-new/reportingservice';
import {
  HTTP_AP_ListAdvisorUsers_ForTable_Request_Filter,
  HTTP_AP_ListAdvisorUsers_ForTable_Request_Sort,
  HTTP_AP_ListReferrals_Request_Filter,
  HTTP_AP_ListReferrals_Request_Sort,
  HTTP_AP_ListTinyUrls_Request_Filter,
  HTTP_AP_ListTinyUrls_Request_Sort,
} from '@api-new/userservice';
import { AffiliateTableRowModel } from '@app/platform/affiliate/models/affiliate-table.model';
import { createAction, props } from '@ngrx/store';
import { OpportunityApprovalRequiredTableRowModel } from '@platform/case/models/opportunity-approval-required-table-row.model';
import { OpportunityTableRowModel } from '@platform/case/models/opportunity-table-row.model';
import { ClientActivityLogTableRowModel } from '@platform/client/pages/client-activity-log-table/models/client-activity-log-table-row.model';
import { ImportTableRowModel } from '@platform/import/models/import-table-row.model';
import { LenderTableRowModel } from '@platform/lender/tables/lender.table-definition';
import { MortgageTableRowModel } from '@platform/mortgage/models/mortgage-table.model';
import { PreCompletionTableRowModel } from '@platform/mortgage/models/pre-completion-table.model';
import { MortgageTableFilterModel } from '@platform/mortgage/pages/mortgage-table/models/mortgage-table-filter.model';
import { ReferralCodeTableRowModel } from '@platform/referral-code/models/referral-code-table.model';
import { TinyUrlTableRowModel } from '@platform/tiny-url/models/tiny-url-table-row.model';
import { UserTableRowModel } from '@platform/user/models/user-table.model';
import { PaginationModel } from '@shared/components/table/models/pagination.model';
import { TablesEnum } from '@shared/store/table/tables.enum';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createAddActions = <Pagination, Sort, Filter, T extends object, TableRows = T[]>(tableName: string): any => {
  const setPagination = createAction(
    `[${tableName}] set pagination`,
    props<{
      pagination: Pagination;
      tableName: TablesEnum;
    }>(),
  );
  const setSorting = createAction(
    `[${tableName}] set sorting`,
    props<{
      sort: Sort;
      tableName: TablesEnum;
    }>(),
  );
  const setFilter = createAction(
    `[${tableName}] set filter`,
    props<{
      filter: Filter;
      tableName: TablesEnum;
    }>(),
  );
  const setSelectedRows = createAction(
    `[${tableName}] set rows`,
    props<{
      entity: TableRows;
      tableName: TablesEnum;
    }>(),
  );
  const resetTableFilters = createAction(
    `[${tableName}] reset table filter`,
    props<{
      entity: Filter;
      tableName: TablesEnum;
    }>(),
  );

  return {
    setPagination,
    setSorting,
    setFilter,
    setSelectedRows,
    resetTableFilters,
  };
};

export const userTableActions = createAddActions<
  PaginationModel,
  HTTP_AP_ListAdvisorUsers_ForTable_Request_Sort,
  Partial<HTTP_AP_ListAdvisorUsers_ForTable_Request_Filter>,
  UserTableRowModel[]
>(TablesEnum.UserTable);

export const outcodeToRegionTableActions = createAddActions<
  PaginationModel,
  HTTP_AP_ListHPIOutcodeToRegionEntries_Request_Sort,
  Partial<HTTP_AP_ListHPIOutcodeToRegionEntries_Request_Filter>,
  HTTP_AP_HPIOutcodeToRegionEntry[]
>(TablesEnum.OutcodeToRegionTable);

export const mortgageTableActions = createAddActions<
  PaginationModel,
  HTTP_AP_ListMortgages_ForTable_Request_Sort,
  Partial<MortgageTableFilterModel>,
  MortgageTableRowModel[]
>(TablesEnum.MortgageTable);

export const clientTableActions = createAddActions<
  PaginationModel,
  HTTP_AP_ListAdvisorUsers_ForTable_Request_Sort,
  Partial<HTTP_AP_ListAdvisorUsers_ForTable_Request_Filter>,
  UserTableRowModel[]
>(TablesEnum.ClientTable);

export const propertyTableActions = createAddActions<
  PaginationModel,
  HTTP_AP_ListProperties_ForTable_Request_Sort,
  Partial<HTTP_AP_ListProperties_ForTable_Request_Filter>,
  HTTP_AP_ListProperties_ForTable_Response_Property[]
>(TablesEnum.PropertyTable);

export const opportunityApproveRequiredTableActions = createAddActions<
  PaginationModel,
  HTTP_AP_ListCases_ForApprovalRequiredTable_Request_Sort,
  HTTP_AP_ListCases_ForApprovalRequiredTable_Request_Filter,
  OpportunityApprovalRequiredTableRowModel[]
>(TablesEnum.OpportunityApprovalRequiredTable);

export const opportunityTableActions = createAddActions<
  PaginationModel,
  HTTP_AP_ListCases_ForOpportunitiesTable_Request_Sort,
  HTTP_AP_ListCases_ForOpportunitiesTable_Request_Filter,
  OpportunityTableRowModel[]
>(TablesEnum.OpportunityTable);

export const mortgageLeadTableActions = createAddActions<
  PaginationModel,
  HTTP_AP_ListCases_ForLeadsTable_Request_Sort,
  HTTP_AP_ListCases_ForLeadsTable_Request_Filter,
  null
>(TablesEnum.MortgagePipelineCaseTable);

export const mortgagePipelineCaseTableActions = createAddActions<
  PaginationModel,
  HTTP_AP_ListCases_ForPipelineTable_Request_Sort,
  HTTP_AP_ListCases_ForPipelineTable_Request_Filter,
  null
>(TablesEnum.MortgagePipelineCaseTable);

export const mortgageClosedCaseTableActions = createAddActions<
  PaginationModel,
  HTTP_AP_ListCases_ForClosedTable_Request_Sort,
  HTTP_AP_ListCases_ForClosedTable_Request_Filter,
  null
>(TablesEnum.MortgageClosedCaseTable);

export const clientCaseTableActions = createAddActions<
  PaginationModel,
  HTTP_AP_ListCases_ForClientDetail_Request_Sort,
  HTTP_AP_ListCases_ForClientDetail_Request_Filter,
  null
>(TablesEnum.ClientCaseTable);

export const companyTableActions = createAddActions<
  PaginationModel,
  HTTP_AP_ListCompanies_ForTable_Request_Sort,
  HTTP_AP_ListCompanies_ForTable_Request_Filter,
  null
>(TablesEnum.CompanyTable);

export const importTableActions = createAddActions<
  PaginationModel,
  HTTP_AP_ListImports_Request_Sort,
  HTTP_AP_ListImports_Request_Filter,
  ImportTableRowModel[]
>(TablesEnum.ImportTable);

export const lenderTableActions = createAddActions<
  PaginationModel,
  HTTP_AP_ListLenders_ForTable_Request_Sort,
  HTTP_AP_ListLenders_ForTable_Request_Filter,
  LenderTableRowModel[]
>(TablesEnum.LenderTable);

export const lenderGroupTableActions = createAddActions<
  PaginationModel,
  HTTP_AP_ListLenderGroups_ForTable_Request_Sort,
  HTTP_AP_ListLenderGroups_ForTable_Request_Filter,
  HTTP_AP_LenderGroup[]
>(TablesEnum.LenderGroupTable);

export const mortgageDocAiTableActions = createAddActions<
  PaginationModel,
  null,
  Partial<HTTP_AP_ListMortgageOfferParserBatches_Request_Filter>,
  null
>(TablesEnum.MortgageDocAiTable);

export const tinyUrlsTableActions = createAddActions<
  PaginationModel,
  HTTP_AP_ListTinyUrls_Request_Sort,
  HTTP_AP_ListTinyUrls_Request_Filter,
  TinyUrlTableRowModel[]
>(TablesEnum.TinyUrlsTable);

export const preCompletionTableActions = createAddActions<
  PaginationModel,
  HTTP_AP_ListPreCompletions_ForTable_Request_Sort,
  HTTP_AP_ListPreCompletions_ForTable_Request_Filter,
  PreCompletionTableRowModel[]
>(TablesEnum.TinyUrlsTable);

export const referralCodesTableActions = createAddActions<
  PaginationModel,
  HTTP_AP_ListReferrals_Request_Sort,
  HTTP_AP_ListReferrals_Request_Filter,
  ReferralCodeTableRowModel[]
>(TablesEnum.ReferralCodeTable);

export const affiliateTableActions = createAddActions<
  PaginationModel,
  undefined,
  HTTP_AP_ListAffiliates_ForTable_Request_Filter,
  AffiliateTableRowModel[]
>(TablesEnum.AffiliateTable);

export const mortgageProductTableActions = createAddActions<
  PaginationModel,
  HTTP_AP_ListMortgageProducts_ForTable_Request_Sort,
  HTTP_AP_ListMortgageProducts_ForTable_Request_Filter,
  HTTP_AP_ListMortgageProducts_ForTable_Response_MortgageProduct[]
>(TablesEnum.MortgageProductTable);

export const networkTableActions = createAddActions<
  PaginationModel,
  undefined,
  undefined,
  HTTP_AP_ListNetworks_ForTable_Response_Network[]
>(TablesEnum.NetworkTable);

export const clientActivityLogTableActions = createAddActions<
  PaginationModel,
  HTTP_AP_ListActivityLogs_Request_Sort,
  HTTP_AP_ListActivityLogs_Request_Filter,
  ClientActivityLogTableRowModel[]
>(TablesEnum.ClientActivityLogTable);
